import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames/bind';
import { isUndefined, get } from 'lodash-es';
import { Modal, Form, Checkbox, Button, message } from '@fuxi/eevee-ui';
import { useAntModal, useAntLocale } from '@fuxi/eevee-hooks';
import { useLoginInfo, specialInnerCodeMap } from '@yaotai/common-utils';
import { formatPhone, validateVerificationCode } from 'common-package/src/utils';

import { CountryPhoneField, PCVerificationCodeInput } from 'common-package/src/components';
import { useYaotaiGlobalContextState } from '@commonPackage/contexts';
import { MobileConflictModal, MobileConflictModalRef } from '@/components';
import API from '@/service';
import styles from './BindMobileModal.module.less';

const cx = classNames.bind(styles);

type Props = {
  onClose?: () => void;
};

export const BindMobileModal = forwardRef<ModalRef, Props>(({ onClose }: Props, ref) => {
  const { t } = useTranslation();
  const { language } = useAntLocale();
  const [form] = Form.useForm();
  const { showModal, closeModal, commonAntModalProps } = useAntModal();
  const { refetchAllAccountInfo } = useYaotaiGlobalContextState();
  const { handleFetchLoginInfo } = useLoginInfo();
  const conflictModalRef = useRef<MobileConflictModalRef>(null);
  const [curCodePhone, setCurCodePhone] = useState('');

  useImperativeHandle(ref, () => {
    return {
      showModal,
    };
  });

  const openConflictModal = () => {
    closeModal();
    conflictModalRef.current?.showModal();
  };

  // 成功绑定手机后的回调
  const handleSuccessClose = () => {
    closeModal();
    form.resetFields();
    onClose?.();
  };

  const validatePhone = async () => {
    await form.validateFields(['phone']);
    const {
      phone: { code, phone },
      code: verificationCodeValue,
    } = form.getFieldsValue();
    /*
     * 用于用户再未获取验证码时已输入验证码，此时会提示 “请先获取验证码”
     * 然后用户点击获取验证码，对验证码输入框进行校验
     */
    if (!isUndefined(verificationCodeValue)) {
      form.validateFields(['code']);
    }
    const codePhone = formatPhone(code, phone);
    setCurCodePhone(codePhone);
    return codePhone;
  };

  // eslint-disable-next-line react-func/max-lines-per-function
  const bindMobile = async () => {
    sessionStorage.setItem('validate_from_bind_mobile_btn', 'true');
    await form.validateFields();
    const {
      phone: { code, phone },
      code: verificationCodeValue,
    } = form.getFieldsValue();
    const res = await API.user.bindMobile({
      phone: formatPhone(code, phone),
      code: verificationCodeValue,
    });
    const errorCode = get(res, 'code');

    if (errorCode === specialInnerCodeMap.bindMobileError) {
      conflictModalRef.current?.setConflictUserInfo({
        ...(get(res, 'detail', {}) as any),
        phone: formatPhone(code, phone),
      });
      form.resetFields();
      openConflictModal();
      return;
    }
    handleFetchLoginInfo();
    refetchAllAccountInfo();
    message.success(t('account.bindSucceedText'));
    handleSuccessClose();
  };

  return (
    <Modal centered footer={null} width={524} {...commonAntModalProps} onCancel={handleSuccessClose}>
      <div className={cx('modal-content')}>
        <div className={cx('title')}>{t('account.bindMobileTile')}</div>
        <div className={cx('tip')}>{t('account.bindMobileTip')}</div>
        <Form form={form} style={{ width: '100%' }}>
          <CountryPhoneField
            hasLabel={false}
            required
            validateTrigger={['onChange']}
            placeholder={t('account.mobilePlaceholder')}
            language={language}
          />
          <Form.Item noStyle dependencies={['phone']}>
            {({ getFieldValue }) => (
              <Form.Item
                name="code"
                validateFirst
                rules={[
                  {
                    validator: validateVerificationCode({
                      codePhone: curCodePhone,
                      phone: formatPhone(getFieldValue('phone').code, getFieldValue('phone').phone),
                      emptyText: t('account.verificationCodePlaceholder'),
                      notGotCodeText: t('account.getCodeFirstText'),
                      errorCodeText: t('account.errorCodeText'),
                      codeFrequentText: t('account.codeFrequentText'),
                      codeFrequentAndLaterTryText: t('account.codeFrequentAndLaterTryText'),
                    }),
                  },
                ]}>
                <PCVerificationCodeInput msgRequest={API.user.getMsgCode} validate={validatePhone} />
              </Form.Item>
            )}
          </Form.Item>
          <Form.Item
            name="agreed"
            style={{ margin: '34px 0 18px' }}
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) => {
                  if (value) return Promise.resolve();
                  else return Promise.reject(t('account.needAgreeText'));
                },
              },
            ]}>
            <div>
              <Checkbox />
              <span className={cx('ml-sm', 'policy-text')}>
                {t('account.policyAgree')}{' '}
                <a href="//reg.163.com/agreement_mobile_ysbh.shtml" target="_blank" rel="noreferrer">
                  {t('account.privacyPolicy')}
                </a>{' '}
                {t('account.andText')}{' '}
                <a href="//reg.163.com/agreement_mobile.shtml" target="_blank" rel="noreferrer">
                  {t('account.userPolicy')}
                </a>
              </span>
            </div>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" className={cx('bind-mobile-btn')} onClick={bindMobile}>
              {t('account.bindMobileBtnText')}
            </Button>
          </Form.Item>
        </Form>
      </div>
      <MobileConflictModal ref={conflictModalRef} onClose={onClose} />
    </Modal>
  );
});

export default BindMobileModal;
